import { IMilestoneTemplate } from "@/interfaces";

export const findMissingProductCategoriesForRequiredApprovers = (
  milestone: IMilestoneTemplate
): string[] => {
  if (!milestone.approvalRequired || milestone.approvalRequired.length === 0) {
    return [];
  }

  // Helper: Check if milestone requires approval for "All Categories"
  // When user checks "All" checkbox, approvalRequiredCheckboxSelectedAll = true
  const milestoneRequiresAllCategories = (
    milestone: IMilestoneTemplate
  ): boolean => milestone.approvalRequiredCheckboxSelectedAll === true;

  // Helper: Check if an approver covers "All Categories" (Product Cat. = All)
  const approverCoversAllCategories = (
    approver: {
      defaultForProductCategories?: string[] | null;
    },
    requiredCategories: string[]
  ): boolean => {
    const cats = approver.defaultForProductCategories;

    // Case 1: null or empty → treated as ALL categories
    if (!cats || cats.length === 0) {
      return true;
    }

    // Case 2: explicit list – treat as ALL if it contains ALL required categories
    if (!requiredCategories || requiredCategories.length === 0) {
      return false;
    }

    if (cats.length < requiredCategories.length) {
      return false;
    }

    const set = new Set(cats);
    return requiredCategories.every((c) => set.has(c));
  };

  // Helper: Check if a project leader approver covers "All Categories"
  // Same logic as regular approvers but uses productCategories field
  const projectLeaderCoversAllCategories = (
    pl: {
      productCategories?: string[] | null;
    },
    requiredCategories: string[]
  ): boolean => {
    const cats = pl.productCategories;

    // Case 1: null or empty → treated as ALL categories
    if (!cats || cats.length === 0) {
      return true;
    }

    // Case 2: explicit list – treat as ALL if it contains ALL required categories
    if (!requiredCategories || requiredCategories.length === 0) {
      return false;
    }

    if (cats.length < requiredCategories.length) {
      return false;
    }

    const set = new Set(cats);
    return requiredCategories.every((c) => set.has(c));
  };

  // Helper: Check if there's at least one approver (regular or project leader) that covers all categories
  const hasAnyApproverCoveringAllCategories = (
    milestone: IMilestoneTemplate,
    requiredCategories: string[]
  ): boolean => {
    const hasGlobalApprover =
      milestone.approvers?.some((a) =>
        approverCoversAllCategories(a, requiredCategories)
      ) ?? false;

    const hasGlobalProjectLeaderApprover =
      milestone.projectLeaderApprovers?.some((pl) =>
        projectLeaderCoversAllCategories(pl, requiredCategories)
      ) ?? false;

    return hasGlobalApprover || hasGlobalProjectLeaderApprover;
  };

  const requiredCategories = milestone.approvalRequired ?? [];

  if (
    milestoneRequiresAllCategories(milestone) &&
    hasAnyApproverCoveringAllCategories(milestone, requiredCategories)
  ) {
    return [];
  }

  if (
    (!milestone.approvers || milestone.approvers.length === 0) &&
    (!milestone.projectLeaderApprovers ||
      milestone.projectLeaderApprovers.length === 0)
  ) {
    return milestone.approvalRequired;
  }

  // Check if there's a "global" approver (covers ALL product categories)
  // A global approver has null/empty defaultForProductCategories OR contains all required categories
  const hasGlobalApprover =
    milestone.approvers?.some((a) =>
      approverCoversAllCategories(a, requiredCategories)
    ) ?? false;

  const hasGlobalProjectLeaderApprover =
    milestone.projectLeaderApprovers?.some((pl) =>
      projectLeaderCoversAllCategories(pl, requiredCategories)
    ) ?? false;

  // If there's a global approver (but milestone doesn't require all), all categories are still covered
  if (hasGlobalApprover || hasGlobalProjectLeaderApprover) {
    return [];
  }

  // Collect all product categories covered by specific approvers
  // NOTE: We do NOT filter by canSetNotApplicable - approvers count regardless of this flag
  const approverProductCategories =
    milestone.approvers?.flatMap((x) => x.defaultForProductCategories ?? []) ??
    [];

  // Collect categories covered by project leader approvers
  const leaderApproverProductCategories =
    milestone.projectLeaderApprovers?.flatMap(
      (x) => x.productCategories ?? []
    ) ?? [];

  // Combine all covered categories into a Set for efficient lookup
  const allApproverProductCategories = new Set([
    ...approverProductCategories,
    ...leaderApproverProductCategories,
  ]);

  // Check if every required category is covered by at least one approver
  const hasRequiredProductCategories = milestone.approvalRequired.every(
    (required) => allApproverProductCategories.has(required)
  );

  if (hasRequiredProductCategories) {
    return [];
  }

  // Return the list of required categories that are NOT covered by any approver
  return milestone.approvalRequired.filter(
    (item) => !allApproverProductCategories.has(item)
  );
};
